<template>
  <label class="ds-toggle">
    <span
      v-if="label && orientation === 'left'"
      class="ds-toggle__label"
      :class="[`ds-toggle__label--${orientation}`, bold && 'ds-font-weight-semi-bold']"
      v-text="label"
    />
    <input
      v-model="checked"
      :aria-checked="checked === true ? 'true' : 'false'"
      role="switch"
      class="ds-toggle__input"
      type="checkbox"
    >
    <div class="ds-toggle__handle" />

    <span
      v-if="label && orientation === 'right'"
      class="ds-toggle__label"
      :class="[`ds-toggle__label--${orientation}`, bold && 'ds-font-weight-semi-bold']"
      v-text="label"
    />
  </label>
</template>

<script setup>
/**
 * A wrapper around a toggle, providing checkbox functionality with a knob-like toggle
 *
 * @usage
 *
 * export default {
 *     components: { Toggle },
 *     data: () => ({ toggleValue: true }),
 *     template: `
 *         <toggle
 *              v-model="toggleValue"`
 *              :label="'Optional label'"
 *              orientation="left"
 *          />
 *     `,
 * }
 */

const checked = defineModel({ type: Boolean, default: false })

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  bold: {
    type: Boolean,
    default: false
  },
  orientation: {
    type: String,
    default: 'right'
  },
  size: {
    type: String,
    default: 'default'
  }
})
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.ds-toggle {
  background: $color-white;
  cursor: pointer;
  display: inline-block;

  &--disabled {
    cursor: not-allowed;
    pointer-events: none;
  }

  &__input {
    position: absolute;
    visibility: hidden;
  }

  &__label {
    position: relative;
    top: 2px;

    &--left {
      margin-right: $spacing-12;
    }

    &--right {
      margin-left: $spacing-12;
    }
  }

  &__handle {
    background: $color-grey-500;
    border-radius: $border-radius-12;
    display: inline-block;
    height: $spacing-24;
    position: relative;
    transition: background 0.25s;
    vertical-align: middle;
    width: 46px;

    &::before {
      background: $color-white;
      border-radius: 50%;
      content: '';
      display: block;
      height: 20px;
      left: 2px;
      position: absolute;
      top: 2px;
      transition: left 0.25s;
      width: 20px;
    }

    .ds-toggle:hover &::before {
      background: $color-white;
    }

    .ds-toggle__input:checked + & {
      background: $color-green-1100;

      &::before {
        left: 24px;
      }
    }

    .ds-toggle__input:disabled + & {
      background: $color-grey-500;
      opacity: 0.6;

      &::before {
        background-image: url('./assets/lock.svg');
        background-position: center;
        background-repeat: no-repeat;
        background-size: 70% 70%;
      }
    }
  }
}
</style>
